/*----------------------------------
- GENERATED FILE
----------------------------------*/

// This file is generated by Proteum from client/pages/marketing/companies.tsx.
// Do not edit it manually.

import type __GeneratedRouteApp from "@/client/index";

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';
import { ArrowRight, Award, Clock, Coins, DollarSign, FileCheck, Search, Sparkles, Target, TrendingUp, UserCheck, Users, X } from 'lucide-preact';

/*----------------------------------
- APP
----------------------------------*/

import Motion, { fadeIn } from '@/client/components/Motion';
import ClientLogosCarousel from '@/client/components/marketing/ClientLogosCarousel';
import PublicFooter from '@/client/components/marketing/PublicFooter';
import PublicHeader from '@/client/components/marketing/PublicHeader';
import Page from '@/client/pages/_layout/_page';
import { MARKETING_URLS } from '@/client/catalogs/marketing/navigation';

/*----------------------------------
- CONTROLEUR
----------------------------------*/

export const __register = (app: __GeneratedRouteApp) => {
  const { Router } = app;
  return Router.page('/companies', { ...({
  static: {
    refresh: '1h',
    urls: ['/companies']
  }
}), ...{ filepath: "/app/client/pages/marketing/companies.tsx", sourceLocation: { line: 24, column: 1 }, id: "marketing_companies" } }, null, ({}) => {
  const [jobTitle, setJobTitle] = React.useState('');
  const [location, setLocation] = React.useState('');
  const [jobDescription, setJobDescription] = React.useState('');
  const [showResults, setShowResults] = React.useState(false);
  const [calendlyOpen, setCalendlyOpen] = React.useState(false);
  return <Page withLayout={false}>
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-violet-50">
                <PublicHeader currentPage="companies" ctaLabel="Request a Demo" ctaHref={MARKETING_URLS.saasDemo} />

                <section className="pt-32 pb-20 px-6 lg:px-8">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <Motion tag="h1" initial="hidden" animate="visible" variants={fadeIn} className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-orange-600 via-pink-600 to-violet-600 bg-clip-text text-transparent mb-6">
                                Find The Perfect Talent
                            </Motion>
                            <Motion tag="p" initial="hidden" animate="visible" variants={fadeIn} transition={{
              delay: 0.1
            }} className="text-xl text-slate-700 mb-8 max-w-3xl mx-auto">
                                Connect with our global network of 10,000+ specialized recruiters. Get matched with experts who understand your industry, geography, and hiring needs.
                            </Motion>
                        </div>

                        <Motion tag="div" initial="hidden" animate="visible" variants={fadeIn} transition={{
            delay: 0.2
          }} className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border-2 border-blue-100 p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                                    <Sparkles className="w-6 h-6 text-white" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">Hiring Simulator</h2>
                            </div>
                            <p className="text-slate-600 mb-6">
                                Get instant insights on salary benchmarks, hiring success rates, and recruiter coverage for your position.
                            </p>

                            <div className="space-y-4 mb-6">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-900 mb-2">Job Title</label>
                                    <input type="text" value={jobTitle} onChange={event => setJobTitle(event.currentTarget.value)} placeholder="Senior Software Engineer" className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-900 mb-2">Location</label>
                                    <input type="text" value={location} onChange={event => setLocation(event.currentTarget.value)} placeholder="London, UK" className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-900 mb-2">Job Description (Optional)</label>
                                    <textarea value={jobDescription} onChange={event => setJobDescription(event.currentTarget.value)} rows={6} placeholder="Paste your job description here for detailed analysis..." className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" />
                                </div>
                            </div>

                            <button type="button" onClick={() => setShowResults(true)} disabled={!jobTitle || !location} className="w-full bg-gradient-to-r from-orange-600 to-violet-600 hover:from-orange-700 hover:to-violet-700 text-white py-4 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                                <Search size={20} />
                                Analyze Position
                            </button>

                            {showResults && <Motion tag="div" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mt-8 space-y-6">
                                    <div className="border-t-2 border-slate-200 pt-6">
                                        <h3 className="text-xl font-bold text-slate-900 mb-4">Analysis Results</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                            {[{
                    icon: DollarSign,
                    title: 'Salary Range',
                    value: '£65,000 - £85,000',
                    note: 'Based on market data',
                    cardClassName: 'from-blue-50 to-cyan-50 border-blue-200',
                    textClassName: 'text-blue-600'
                  }, {
                    icon: TrendingUp,
                    title: 'Success Rate',
                    value: '78%',
                    note: 'For similar positions',
                    cardClassName: 'from-green-50 to-emerald-50 border-green-200',
                    textClassName: 'text-green-600'
                  }, {
                    icon: Clock,
                    title: 'Time to Hire',
                    value: '28 days',
                    note: 'Average placement time',
                    cardClassName: 'from-orange-50 to-amber-50 border-orange-200',
                    textClassName: 'text-orange-600'
                  }, {
                    icon: Award,
                    title: 'Recruiter Coverage',
                    value: '4 experts',
                    note: 'Matched to your brief',
                    cardClassName: 'from-purple-50 to-pink-50 border-purple-200',
                    textClassName: 'text-purple-600'
                  }].map(stat => <div key={stat.title} className={`bg-gradient-to-br ${stat.cardClassName} rounded-xl p-6 border`}>
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <stat.icon className={`w-5 h-5 ${stat.textClassName}`} />
                                                        <span className="font-semibold text-slate-900">{stat.title}</span>
                                                    </div>
                                                    <p className={`text-2xl font-bold ${stat.textClassName}`}>{stat.value}</p>
                                                    <p className="text-sm text-slate-600 mt-1">{stat.note}</p>
                                                </div>)}
                                        </div>

                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                                                <Users className="w-5 h-5 text-blue-600" />
                                                4 Specialized Recruiters Available
                                            </h4>
                                            <div className="space-y-3">
                                                {[{
                      specialty: 'Tech Recruitment UK',
                      successRate: '85%',
                      placements: 127,
                      avgTime: '24 days'
                    }, {
                      specialty: 'Software Engineers London',
                      successRate: '82%',
                      placements: 93,
                      avgTime: '26 days'
                    }, {
                      specialty: 'Senior Tech Roles',
                      successRate: '79%',
                      placements: 156,
                      avgTime: '30 days'
                    }, {
                      specialty: 'Engineering Leadership',
                      successRate: '76%',
                      placements: 68,
                      avgTime: '32 days'
                    }].map(recruiter => <div key={recruiter.specialty} className="bg-white rounded-lg p-4 border border-slate-200 hover:border-blue-300 transition-all">
                                                        <div className="flex items-center justify-between mb-2 gap-4">
                                                            <span className="font-semibold text-slate-900">{recruiter.specialty}</span>
                                                            <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                                                                {recruiter.successRate} success
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-4 text-sm text-slate-600">
                                                            <span>{recruiter.placements} placements</span>
                                                            <span>•</span>
                                                            <span>Avg: {recruiter.avgTime}</span>
                                                        </div>
                                                    </div>)}
                                            </div>
                                        </div>

                                        <button type="button" onClick={() => setCalendlyOpen(true)} className="w-full mt-6 bg-gradient-to-r from-orange-600 to-violet-600 hover:from-orange-700 hover:to-violet-700 text-white py-4 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
                                            Post This Position
                                            <ArrowRight size={20} />
                                        </button>
                                    </div>
                                </Motion>}
                        </Motion>
                    </div>
                </section>

                <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-white">
                    <div className="max-w-6xl mx-auto">
                        <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="text-center mb-16">
                            <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-4">
                                What Makes Us Different
                            </h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                We go beyond traditional recruitment platforms to ensure your success.
                            </p>
                        </Motion>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[{
              icon: UserCheck,
              title: 'Dedicated Customer Success Manager',
              description: 'A dedicated CSM configures your search and acts as the interface with our recruiters.',
              iconClassName: 'from-blue-500 to-cyan-500'
            }, {
              icon: Coins,
              title: 'Adaptive Engagement',
              description: 'The engagement model adapts to your situation, from a single role to global volume hiring.',
              iconClassName: 'from-emerald-500 to-teal-500'
            }, {
              icon: FileCheck,
              title: 'Standardized Reports and Negotiation Support',
              description: 'Compare candidates easily and get support during negotiations to secure top talent.',
              iconClassName: 'from-violet-500 to-purple-500'
            }].map((item, index) => <Motion tag="div" key={item.title} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeIn} transition={{
              delay: index * 0.1
            }} className="bg-white rounded-2xl p-8 shadow-lg border-2 border-blue-100 hover:border-blue-300 hover:shadow-2xl transition-all">
                                    <div className={`w-16 h-16 bg-gradient-to-br ${item.iconClassName} rounded-2xl flex items-center justify-center mb-6`}>
                                        <item.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{item.title}</h3>
                                    <p className="text-slate-700 leading-relaxed">{item.description}</p>
                                </Motion>)}
                        </div>
                    </div>
                </section>

                <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-white to-blue-50">
                    <div className="max-w-6xl mx-auto">
                        <Motion tag="h2" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="text-4xl font-bold text-center bg-gradient-to-r from-orange-600 to-violet-600 bg-clip-text text-transparent mb-12">
                            Why Companies Choose CrossPath
                        </Motion>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[{
              icon: Users,
              title: 'Expert Matching',
              description: 'Get connected with recruiters who specialize in your industry, location, and role level.',
              stat: '10,000+ Recruiters'
            }, {
              icon: Clock,
              title: 'Faster Hiring',
              description: 'Average time to first candidate: 3 days with multiple recruiters on your position simultaneously.',
              stat: '70% Interview Rate'
            }, {
              icon: Award,
              title: 'Quality Guaranteed',
              description: 'All candidates are pre-vetted with full transparency in one dashboard.',
              stat: 'Full Transparency'
            }].map((benefit, index) => <Motion tag="div" key={benefit.title} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeIn} transition={{
              delay: index * 0.1
            }} className="bg-white rounded-2xl p-8 border-2 border-blue-100 hover:border-blue-300 hover:shadow-xl transition-all">
                                    <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl flex items-center justify-center mb-6">
                                        <benefit.icon className="w-7 h-7 text-blue-600" />
                                    </div>
                                    <div className="mb-3">
                                        <span className="inline-block bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                                            {benefit.stat}
                                        </span>
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                                    <p className="text-slate-700 leading-relaxed">{benefit.description}</p>
                                </Motion>)}
                        </div>
                    </div>
                </section>

                <ClientLogosCarousel />

                <section className="py-24 px-6 lg:px-8 bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600">
                    <div className="max-w-4xl mx-auto text-center">
                        <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn}>
                            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                                Ready to Find Your Next Hire?
                            </h2>
                            <p className="text-xl text-white/90 mb-8">Post your position and get matched with specialized recruiters today</p>
                            <button type="button" onClick={() => setCalendlyOpen(true)} className="bg-white text-blue-600 hover:bg-blue-50 px-10 py-4 rounded-xl font-semibold text-lg transition-all shadow-2xl hover:shadow-2xl hover:scale-105 transform inline-flex items-center gap-2">
                                Post a Position
                                <ArrowRight size={20} />
                            </button>
                        </Motion>
                    </div>
                </section>

                {calendlyOpen && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
                        <div className="relative h-[875px] w-full max-w-2xl rounded-xl bg-white shadow-2xl">
                            <button type="button" onClick={() => setCalendlyOpen(false)} className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-500 shadow-lg transition-colors hover:text-slate-900" aria-label="Close Calendly modal">
                                <X size={20} />
                            </button>
                            <iframe src={MARKETING_URLS.jordanCalendly} title="Jordan Calendly" width="100%" height="100%" frameBorder="0" className="rounded-xl" />
                        </div>
                    </div>}

                <PublicFooter tagline="Hire faster with recruiter matching, guided delivery, and full pipeline visibility from one place." />
            </div>
        </Page>;
});
};
